import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import { getTimeZone } from "api/payments";
import React, { useEffect } from "react";
import { MdHighlightOff } from "react-icons/md";
import { MultiSelect } from "react-multi-select-component";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

interface option { label: string; value: string; }

const STATUSES: option[] = [
  "APPROVED","REFUNDED","AUTHORIZED","CAPTURED","PENDING","CANCELLED",
  "DECLINED","ERRORED","REFUND DECLINED","CHARGEBACK","REJECTED","ALERTED",
  "HELD","DUPLICATE","DISPUTE","FAILED",
].map((s) => ({ label: s, value: s }));

type PayoutFilter = {
  status?: string;
  client: string[];
  dates: Date[] | null;
  start_date: any;
  end_date: Date[] | null;
  payment_status: string[];
  card_type: string[];
  gateway: string[];
  timezone: string;
};

const FilterModal = ({
  clients, allGateways, allCardType, paymentType, curretClient, setCurrentClient,
  setPage, handleSelectPayment, filterData, fetchPayments, varient,
  filter, setFilter, setClearClick, roleData, setLocalFilters, localFilters, page, isAllDataReady,
}: {
  clients?: any; allGateways?: any; allCardType?: string[];
  paymentType?: any; curretClient?: any; setCurrentClient?: any; setPage?: any;
  handleSelectPayment?: (e: any) => void; filterData?: () => void; fetchPayments?: () => void;
  varient?: string; filter?: any; setFilter?: React.Dispatch<React.SetStateAction<PayoutFilter>>;
  setClearClick?: () => void; roleData?: any;
  setLocalFilters?: (e: any) => void; localFilters?: any; page?: any; isAllDataReady?: any;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [CardOption, setCardOption] = React.useState<option[]>([]);
  const [timeZone, setTimeZone] = React.useState<any>();
  const [selectedStatus, setSelectedStatus] = React.useState<option[]>([]);
  const [seletedGateways, setSelectedGateways] = React.useState<option[]>([]);
  const [selectedCardType, setSelectedCardType] = React.useState<option[]>([]);
  const [isCloseModal, setIsCloseModal] = React.useState(false);
  const [isApplied, setIsApplied] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);

  React.useEffect(() => {
    if (allCardType !== undefined) setCardOption(allCardType.map((i) => ({ label: i, value: i })));
  }, [allCardType]);

  useEffect(() => {
    getTimeZone().then((d) => setTimeZone(d ?? [])).catch(() => {});
  }, []);

  const hasActiveFilter =
    filter?.gateway?.length > 0 || filter?.client?.length > 0 ||
    filter?.dates || filter?.payment_status?.length > 0 ||
    filter?.card_type?.length > 0 || filter?.timezone;

  const activeCount = [
    filter?.client?.length > 0, filter?.dates,
    filter?.payment_status?.length > 0, filter?.gateway?.length > 0,
    filter?.timezone, filter?.card_type?.length > 0,
  ].filter(Boolean).length;

  const handleClose = () => {
    if (isDirty && !isApplied) {
      setFilter?.({ client: [], dates: null, end_date: null, start_date: null, payment_status: [], card_type: [], gateway: [], timezone: "" });
      setSelectedCardType([]); setSelectedStatus([]); setSelectedGateways([]);
    }
    setIsDirty(false);
    setIsCloseModal(true);
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 320);
  };

  const handleClear = () => {
    setFilter?.((prev: any) => ({
      status: prev?.status ?? "all", client: [], dates: null,
      payment_status: [], card_type: [], gateway: [], timezone: "",
      end_date: null, start_date: null,
    }));
    setSelectedCardType([]); setSelectedGateways([]); setSelectedStatus([]);
    setIsApplied(false); setClearClick?.();
  };

  return (
    <>
      <button
        className="af-trigger-btn"
        onClick={() => { setIsApplied(false); onOpen(); }}
        disabled={!isAllDataReady}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
        </svg>
        {hasActiveFilter ? "Update Filter" : "Apply Filter"}
        {hasActiveFilter && <span className="af-trigger-badge">{activeCount}</span>}
      </button>

      {hasActiveFilter && (
        <button className="af-clear-btn" onClick={handleClear}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
          Clear Filter
        </button>
      )}

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />

        <div className={`afm-panel${isOpen && !isCloseModal ? " afm-panel--open" : " afm-panel--close"}`}>
          <div className="afm-top-glow" />

          {/* Header */}
          <div className="afm-header">
            <div className="afm-header-brand">
              <div className="afm-header-logo">
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
              </div>
              <div>
                <h2 className="afm-header-title">Filter Payouts</h2>
                <p className="afm-header-sub">Narrow results by client, date, status and more</p>
              </div>
            </div>
            <button className="afm-close-btn" onClick={handleClose} aria-label="Close">
              <MdHighlightOff size={22} />
            </button>
          </div>

          {/* Body */}
          <div className="afm-body">

            {/* Clients */}
            <div className="afm-section">
              <div className="afm-section-icon afm-section-icon--blue">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <div className="afm-section-content">
                <label className="afm-label">Clients</label>
                <p className="afm-hint">Filter by one or more clients</p>
                <MultiSelect
                  options={clients?.[0]?.map((m: any) => ({ label: m?.name, value: m?.client_id })) ?? []}
                  value={clients?.[0]?.map((m: any) => ({ label: m?.name, value: m?.client_id })).filter((f: any) => filter?.client?.includes(f?.value)) ?? []}
                  onChange={(e: any) => {
                    setIsDirty(true);
                    setFilter?.((p: any) => ({ ...p, client: e.map((m: any) => m?.value) }));
                    setLocalFilters?.((p: any) => ({ ...p, client: e.map((m: any) => m?.value) }));
                  }}
                  labelledBy="Select clients" isCreatable={true}
                />
              </div>
            </div>

            <div className="afm-divider" />

            {/* Date Range */}
            <div className="afm-section">
              <div className="afm-section-icon afm-section-icon--purple">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <div className="afm-section-content">
                <label className="afm-label">Date Range</label>
                <p className="afm-hint">Filter payouts within a specific date range</p>
                <div className="afm-datepicker-wrap">
                  <DatePicker
                    onKeyDown={(e) => e.preventDefault()}
                    className="afm-datepicker"
                    placeholderText="Select date range…"
                    startDate={filter?.start_date ? new Date(filter?.start_date) : null}
                    endDate={filter?.end_date ? new Date(filter?.end_date) : null}
                    selectsRange={true}
                    isClearable={true}
                    onChange={(dateRange) => {
                      setIsDirty(true);
                      const [a, b] = dateRange as [Date | null, Date | null];
                      setFilter?.((p: any) => ({
                        ...p, start_date: a, end_date: b,
                        dates: a != null && b != null ? [a, b] : null,
                      }));
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="afm-divider" />

            {/* Payment Status */}
            <div className="afm-section">
              <div className="afm-section-icon afm-section-icon--green">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <div className="afm-section-content">
                <label className="afm-label">Payout Status</label>
                <p className="afm-hint">Filter by one or more payout statuses</p>
                <MultiSelect
                  options={STATUSES} value={selectedStatus}
                  onChange={(e: any) => {
                    setIsDirty(true); setSelectedStatus(e);
                    setFilter?.({ ...filter, payment_status: e.map((i: option) => i.value) });
                  }}
                  labelledBy="Select status" isCreatable={true}
                />
              </div>
            </div>

            <div className="afm-divider" />

            {/* Gateway */}
            <div className="afm-section">
              <div className="afm-section-icon afm-section-icon--sky">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2"/>
                  <line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
              </div>
              <div className="afm-section-content">
                <label className="afm-label">Gateway</label>
                <p className="afm-hint">Filter by payment gateway</p>
                {allGateways ? (
                  <MultiSelect
                    options={allGateways.map((i: any) => ({
                      label: roleData?.[0]?.payout?.value?.payout_show_method_name ? i.name : i.id,
                      value: i.id,
                    }))}
                    value={seletedGateways}
                    onChange={(e: any) => {
                      setIsDirty(true); setSelectedGateways(e);
                      setFilter?.({ ...filter, gateway: e.map((i: option) => i.value) });
                    }}
                    labelledBy="Select gateway" isCreatable={true}
                  />
                ) : (
                  <p className="afm-loading">Loading gateways…</p>
                )}
              </div>
            </div>

            <div className="afm-divider" />

            {/* Timezone */}
            <div className="afm-section">
              <div className="afm-section-icon afm-section-icon--amber">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div className="afm-section-content">
                <label className="afm-label">Timezone</label>
                <p className="afm-hint">Select the timezone for date filtering</p>
                <select
                  className="afm-select"
                  name="timezone"
                  value={filter?.timezone ?? ""}
                  onChange={(e) => { setIsDirty(true); setFilter?.({ ...filter, timezone: e.target.value }); }}
                >
                  <option value="">Select timezone…</option>
                  {timeZone?.map((d: any) => (
                    <option key={d.label} value={d.label}>{`${d.label} (${d.value})`}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Active filter summary */}
            {hasActiveFilter && (
              <div className="afm-summary">
                <p className="afm-summary-label">Active filters:</p>
                <div className="afm-summary-chips">
                  {filter?.client?.length > 0 && <span className="afm-chip afm-chip--blue">Clients ({filter.client.length})</span>}
                  {filter?.dates && <span className="afm-chip afm-chip--purple">Date range set</span>}
                  {filter?.payment_status?.length > 0 && <span className="afm-chip afm-chip--green">Status ({filter.payment_status.length})</span>}
                  {filter?.gateway?.length > 0 && <span className="afm-chip afm-chip--sky">Gateways ({filter.gateway.length})</span>}
                  {filter?.timezone && <span className="afm-chip afm-chip--amber">Timezone set</span>}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="afm-footer">
            <div className="afm-footer-note">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              Click Apply to update results
            </div>
            <div className="afm-footer-btns">
              <button className="afm-btn-cancel" onClick={handleClose}>Cancel</button>
              <button
                className="afm-btn-apply"
                onClick={() => {
                  setPage?.(1); filterData?.();
                  setIsApplied(true); setIsDirty(false); onClose();
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .af-trigger-btn {
          position: relative; display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 700; color: #fff;
          padding: 9px 16px; border-radius: 10px; border: none;
          background: linear-gradient(135deg,#1d4ed8,#2563eb);
          cursor: pointer; box-shadow: 0 3px 12px rgba(37,99,235,.28);
          transition: transform .15s, box-shadow .18s;
        }
        .af-trigger-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(37,99,235,.36); }
        .af-trigger-btn:disabled { opacity: .5; cursor: not-allowed; }
        .af-trigger-badge {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 18px; height: 18px; padding: 0 5px;
          border-radius: 9px; background: #fbbf24; color: #1e3a8a;
          font-size: 10px; font-weight: 800; border: 1.5px solid rgba(255,255,255,.7);
        }
        .af-clear-btn {
          display: inline-flex; align-items: center; gap: 5px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; color: #dc2626;
          padding: 9px 16px; border-radius: 10px;
          background: #fff7f7; border: 1.5px solid #fecaca; cursor: pointer; transition: all .15s;
        }
        .af-clear-btn:hover { background: #fee2e2; border-color: #fca5a5; }

        .afm-panel {
          font-family: 'Plus Jakarta Sans', sans-serif;
          position: fixed; top: 0; right: 0; bottom: 0;
          width: 100%; max-width: 520px; background: #ffffff;
          z-index: 1004; display: flex; flex-direction: column;
          box-shadow: -12px 0 60px rgba(15,23,42,.18);
          transition: transform .35s cubic-bezier(0.16,1,0.3,1); overflow: hidden;
        }
        .afm-panel--open  { transform: translateX(0); }
        .afm-panel--close { transform: translateX(100%); }

        .afm-top-glow {
          height: 3px;
          background: linear-gradient(90deg,#0f2744,#2563eb,#7c3aed,#2563eb,#0f2744);
          background-size: 200% 100%; animation: afmGlow 3s linear infinite; flex-shrink: 0;
        }
        @keyframes afmGlow { 0%{background-position:0%} 100%{background-position:200%} }

        .afm-header {
          padding: 15px 26px;
          background: linear-gradient(135deg,#0f2744 0%,#1d4ed8 60%,#2563eb 100%);
          display: flex; align-items: center; justify-content: space-between; flex-shrink: 0;
        }
        .afm-header-brand { display: flex; align-items: center; gap: 13px; }
        .afm-header-logo {
          width: 42px; height: 42px; border-radius: 12px;
          background: rgba(255,255,255,.15); border: 1.5px solid rgba(255,255,255,.25);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .afm-header-title { font-size: 16px; font-weight: 800; color: #fff; letter-spacing: -.3px; margin-bottom: 2px; line-height: 1; }
        .afm-header-sub { font-size: 12px; color: rgba(255,255,255,.55); font-weight: 500; }
        .afm-close-btn {
          background: none; border: none; cursor: pointer;
          color: rgba(255,255,255,.65); display: flex; align-items: center; padding: 2px; transition: color .15s;
        }
        .afm-close-btn:hover { color: #fff; }

        .afm-body {
          flex: 1; padding: 20px 26px; overflow-y: auto;
          display: flex; flex-direction: column; gap: 4px;
        }
        .afm-body::-webkit-scrollbar { width: 4px; }
        .afm-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

        .afm-section { display: flex; gap: 16px; align-items: flex-start; padding: 16px 0; }
        .afm-section-icon {
          width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center; margin-top: 2px;
        }
        .afm-section-icon--blue   { background: linear-gradient(135deg,#eff6ff,#dbeafe); border: 1.5px solid #bfdbfe; color: #2563eb; }
        .afm-section-icon--purple { background: linear-gradient(135deg,#faf5ff,#ede9fe); border: 1.5px solid #ddd6fe; color: #7c3aed; }
        .afm-section-icon--green  { background: linear-gradient(135deg,#f0fdf4,#dcfce7); border: 1.5px solid #bbf7d0; color: #16a34a; }
        .afm-section-icon--sky    { background: linear-gradient(135deg,#f0f9ff,#e0f2fe); border: 1.5px solid #bae6fd; color: #0284c7; }
        .afm-section-icon--amber  { background: linear-gradient(135deg,#fffbeb,#fef3c7); border: 1.5px solid #fde68a; color: #d97706; }

        .afm-section-content { flex: 1; display: flex; flex-direction: column; gap: 6px; }
        .afm-label { font-size: 13px; font-weight: 700; color: #0f172a; }
        .afm-hint { font-size: 12px; color: #94a3b8; font-weight: 500; margin-bottom: 2px; }
        .afm-loading { font-size: 12.5px; color: #94a3b8; font-style: italic; }
        .afm-divider { height: 1px; background: #f1f5f9; }

        .afm-select {
          width: 100%; padding: 11px 13px;
          background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 11px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; color: #0f172a;
          outline: none; cursor: pointer;
        }
        .afm-select:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.1); }

        .afm-datepicker-wrap .react-datepicker-wrapper { width: 100%; }
        .afm-datepicker {
          width: 100%; padding: 11px 14px;
          background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 11px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; color: #0f172a;
          outline: none; transition: border-color .18s, box-shadow .18s; box-sizing: border-box;
        }
        .afm-datepicker:focus { border-color: #2563eb; background: #fff; box-shadow: 0 0 0 3px rgba(37,99,235,.1); }
        .afm-datepicker::placeholder { color: #cbd5e1; }

        .afm-summary {
          margin-top: 8px; padding: 14px 16px;
          background: linear-gradient(135deg,#eff6ff,#dbeafe);
          border: 1.5px solid #bfdbfe; border-radius: 12px;
        }
        .afm-summary-label { font-size: 11px; font-weight: 700; color: #1d4ed8; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 9px; }
        .afm-summary-chips { display: flex; flex-wrap: wrap; gap: 6px; }
        .afm-chip { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; font-weight: 600; padding: 4px 10px; border-radius: 7px; }
        .afm-chip--blue   { background: #eff6ff; border: 1.5px solid #bfdbfe; color: #1d4ed8; }
        .afm-chip--purple { background: #ede9fe; border: 1.5px solid #c4b5fd; color: #6d28d9; }
        .afm-chip--green  { background: #f0fdf4; border: 1.5px solid #bbf7d0; color: #15803d; }
        .afm-chip--sky    { background: #f0f9ff; border: 1.5px solid #bae6fd; color: #0369a1; }
        .afm-chip--amber  { background: #fffbeb; border: 1.5px solid #fde68a; color: #b45309; }

        .afm-footer {
          padding: 14px 26px; border-top: 1.5px solid #f1f5f9; background: #fafbfc;
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 10px; flex-shrink: 0;
        }
        .afm-footer-note { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #94a3b8; font-weight: 500; }
        .afm-footer-btns { display: flex; align-items: center; gap: 8px; }
        .afm-btn-cancel {
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; color: #64748b;
          padding: 9px 16px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff;
          cursor: pointer; transition: all .15s;
        }
        .afm-btn-cancel:hover { background: #f1f5f9; color: #374151; }
        .afm-btn-apply {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
          padding: 10px 22px; border-radius: 10px; border: none;
          background: linear-gradient(135deg,#1d4ed8,#2563eb);
          cursor: pointer; box-shadow: 0 4px 14px rgba(37,99,235,.3);
          transition: transform .15s, box-shadow .18s; position: relative; overflow: hidden;
        }
        .afm-btn-apply:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(37,99,235,.4); }

        @media (max-width: 540px) {
          .afm-panel { max-width: 100%; }
          .afm-header, .afm-body, .afm-footer { padding-left: 18px; padding-right: 18px; }
        }
      `}</style>
    </>
  );
};

export default FilterModal;